<template>
	<div
		v-if="!hideSummary"
		id="summary"
		ref="summary"
		class="summary"
		:style="{ height, transition }"
	>
		<div class="row">
			<div class="col-12 mb-4">
				<h3 class="title-4 text-grey-800 mb-2">
					{{ stepNum }}{{ store.state.content.planningSummary_sectionTitle }}
				</h3>
				<div
					class="summary-wrapper bg-white px-3 pt-3 pb-2 p-sm-4 planning-bottom mt-4 shadow-4"
				>
					<Accommodations />
					<BorderHorizontal />
					<!-- ---------------------- -->
					<Options :variant="anySelectedVariant" />
					<BorderHorizontal />
					<!-- ---------------------- -->
					<Total />
					<Fees />
					<BottomButtons />
				</div>
			</div>
		</div>
		<StickySummary />
	</div>
</template>
  
<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, toRef, watch } from "vue";
import { useStore } from "vuex";
import hasTransportOptions from "@/vue/planning/computed/hasTransportOptions";

// components
import Fees from "@/vue/planning/components/summary/fees.vue";
import Accommodations from "@/vue/planning/components/summary/accommodations.vue";
import BorderHorizontal from "@/vue/common/borderHorizontal.vue";
import Options from "@/vue/planning/components/summary/options.vue";
import Total from "@/vue/planning/components/summary/total.vue";
import BottomButtons from "@/vue/planning/components/summary/bottomButtons.vue";
import StickySummary from "@/vue/planning/components/stickySummary.vue";

const store = useStore();

// define props (transition only)
const props = withDefaults(
	defineProps<{ transition?: string }>(),
	{ transition: "height 250ms ease-in-out" }
);
  
// keep `transition` reactive using toRef
const transition = toRef(props, "transition");

const summary = ref<HTMLElement | null>(null);
// single source of truth for the accordion height; starts collapsed so the
// summary is hidden on launch (no flash) until there are selections.
const height = ref<string>("0px");
// tracks open/closed so expand/collapse are idempotent (no expand-then-collapse
// flash when syncing an already-collapsed summary on first render)
const expanded = ref(false);
const speed = 250;

// if there is no product available, don't show the summary
const hideSummary = computed(() => {
	return Object.keys(store.getters.wiypCall).length === 0;
});

// compute the step number (3. or 4.)
const stepNum = computed(() => {
	return hasTransportOptions.value && store.state.screenType === "desktop"
		? "4. "
		: "3. ";
});

// compute the first selected prestation variant
const anySelectedVariant = computed(() => {
	const selected = store.getters.selectedPrestations;
	return selected && selected.length > 0 ? selected[0].prestation : undefined;
});

const totalSelectedHebergements = computed<number>(() => store.getters.totalSelectedHebergements);

function expand() {
	const el = summary.value;
	if (!el || expanded.value) return;
	expanded.value = true;
	height.value = el.scrollHeight + "px";
	// after the transition, switch to auto so dynamic content can grow
	window.setTimeout(() => {
		if (expanded.value) height.value = "auto";
	}, speed);
}

async function collapse() {
	const el = summary.value;
	if (!el || !expanded.value) return;
	expanded.value = false;
	// pin the current pixel height first so the transition to 0 animates
	height.value = el.scrollHeight + "px";
	await nextTick();
	height.value = "0px";
}

// expand when there are selections, collapse otherwise
function syncSummary() {
	if (hideSummary.value) return;
	if (totalSelectedHebergements.value > 0) {
		expand();
	} else {
		collapse();
	}
}

watch(totalSelectedHebergements, () => {
	nextTick(() => {
		syncSummary();
	});
});

// `wiypCall` usually arrives after mount, so the `v-if` element isn't in the
// DOM during onMounted. Sync once it becomes visible.
watch(hideSummary, (hidden) => {
	if (!hidden) {
		nextTick(() => {
			syncSummary();
		});
	}
});

onMounted(() => {
	if (!hideSummary.value) {
		nextTick(() => {
			syncSummary();
		});
	}
});
</script>
  
  <style scoped>
  .summary {
	overflow: hidden;
  }
  .summary-wrapper {
	box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.15);
	border-radius: var(--border-radius-card-secondary);
	padding: 35px;
  }
  </style>
